import {
  Aperture,
  Book,
  Box,
  Castle,
  ChevronDown,
  ChevronUp,
  DoorOpen,
  Factory,
  FlaskConical,
  Pencil,
  Plus,
  RadioTower,
  Swords,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  deleteOwner,
  deleteStructure,
  patchStructure,
  patchSystemAnnotations,
  type SystemAnnotationsPatch,
} from "../api/chains";
import { useCurrentSystem } from "../hooks/useCurrentSystem";
import { useStaticSystemsById } from "../hooks/useStaticSystems";
import { useSystemRecentKills } from "../hooks/useSystemRecentKills";
import { isEditableTarget } from "../lib/isEditableTarget";
import { STRUCTURE_KINDS } from "../lib/structureKinds";
import { CLASS_LABEL } from "../lib/systemClass";
import { useChainStore } from "../store/chainStore";
import { useLayoutStore } from "../store/layoutStore";
import {
  isJspaceClass,
  type StaticSystem,
  type StructureKind,
  type System,
  type SystemActivity,
  type SystemHostility,
  type SystemOwner,
  type SystemStructure,
} from "../types/chain";
import { TRADE_HUB_NAMES, tradeHubDisplay } from "../utils/closestHub";
import { EntityLogo } from "./EntityLogo";
import { OwnerDialog } from "./OwnerDialog";
import { StructureDialog } from "./StructureDialog";
import { SystemIdentity } from "./SystemIdentity";

const HOSTILITY_BADGES: { value: SystemHostility; label: string }[] = [
  { value: "hostile", label: "hostile" },
  { value: "pvp_friendly", label: "pvp" },
  { value: "friendly", label: "friendly" },
];
const ACTIVITIES: SystemActivity[] = ["active", "dormant"];

const STRUCTURE_ICONS: Record<StructureKind, LucideIcon> = {
  Astrahus: Castle,
  Fortizar: Castle,
  Keepstar: Castle,
  Athanor: FlaskConical,
  Tatara: FlaskConical,
  Raitaru: Factory,
  Azbel: Factory,
  Sotiyo: Factory,
  POS: RadioTower,
  other: Box,
};

const NOTES_DEBOUNCE_MS = 600;

const C1_TO_C6_CLASS = /^C[1-6]$/;

type UpdateSystem = (id: string, patch: Partial<System>) => void;

function latestTimestamp(values: (string | null | undefined)[]): string | null {
  let best: string | null = null;
  let bestTime = -Infinity;
  for (const value of values) {
    if (!value) continue;
    const time = new Date(value).getTime();
    if (Number.isNaN(time)) continue;
    if (time > bestTime) {
      bestTime = time;
      best = value;
    }
  }
  return best;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  });
}

export function SystemPanel() {
  const chainId = useChainStore((s) => s.chainId);
  const systems = useChainStore((s) => s.systems);
  const highlightedSystemId = useLayoutStore((s) => s.highlightedSystemId);
  const currentSystemId = useCurrentSystem().data?.id ?? null;
  const { byId: staticById } = useStaticSystemsById();
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (chainId == null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      if (event.key === "w" || event.key === "W") {
        event.preventDefault();
        setCollapsed((c) => !c);
        return;
      }
      if (event.key === "Escape") {
        const target = event.target;
        if (target instanceof Element && target.closest('[role="dialog"]')) {
          return;
        }
        setCollapsed(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [chainId]);

  const targetSystemId = highlightedSystemId ?? currentSystemId;
  const targetSystem = systems.find((s) => s.id === targetSystemId) ?? null;
  const targetStatic = targetSystem ? (staticById(targetSystem.id) ?? null) : null;
  const effect = targetStatic?.effect ?? null;
  const recentKills = useSystemRecentKills(
    chainId == null ? null : (targetSystem?.id ?? null),
  );

  if (chainId == null) return null;

  const recentKillsLabel = recentKills.isLoading
    ? "…"
    : recentKills.isError || !recentKills.data
      ? "—"
      : recentKills.data.capped
        ? `${recentKills.data.count}+`
        : String(recentKills.data.count);
  const recentKillsHint =
    recentKills.data && !recentKills.isError
      ? recentKills.data.capped
        ? `${recentKills.data.count}+ kills in last 24h`
        : `${recentKills.data.count} kills in last 24h`
      : null;
  const gatecampDisplay =
    targetStatic &&
    (targetStatic.class === "HS" || targetStatic.class === "LS") &&
    !TRADE_HUB_NAMES.has(targetStatic.name)
      ? tradeHubDisplay(targetStatic.hub_jumps)
      : null;
  const gatecampHubs = gatecampDisplay
    ? [
        gatecampDisplay.closest,
        ...(gatecampDisplay.jita
          ? [{ hub: "Jita" as const, jumps: gatecampDisplay.jita.jumps }]
          : []),
      ]
    : [];

  return (
    <aside
      className={`system-panel${collapsed ? " system-panel--minimized" : ""}`}
      role="complementary"
      aria-label="System"
    >
      <header className="system-panel__header">
        <h2 className="system-panel__heading">
          {targetSystem && targetStatic ? (
            <SystemIdentity
              systemClass={targetStatic.class}
              name={targetStatic.name}
              effect={effect}
              plainName
              effectAsText
            >
              <span className="system-panel__links">
                <a
                  href={`https://zkillboard.com/system/${targetSystem.id}/`}
                  target="_blank"
                  rel="noreferrer"
                  className="system-panel__icon-btn system-panel__icon-btn--zkill"
                  aria-label={
                    recentKillsHint
                      ? `Open zKillboard for ${targetStatic.name} — ${recentKillsHint}`
                      : `Open zKillboard for ${targetStatic.name}`
                  }
                  title={
                    recentKillsHint
                      ? `Open zKillboard for ${targetStatic.name} — ${recentKillsHint}`
                      : `Open zKillboard for ${targetStatic.name}`
                  }
                >
                  <Swords size={16} aria-hidden />
                  <span className="system-panel__icon-btn-label">
                    {recentKillsLabel}
                  </span>
                </a>
                {isJspaceClass(targetStatic.class) && (
                  <a
                    href={`https://anoik.is/systems/${targetStatic.name}`}
                    target="_blank"
                    rel="noreferrer"
                    className="system-panel__icon-btn system-panel__icon-btn--anoik"
                    aria-label={`Open Anoik.is for ${targetStatic.name}`}
                    title={`Open Anoik.is for ${targetStatic.name}`}
                  >
                    <Aperture size={16} aria-hidden />
                  </a>
                )}
                {C1_TO_C6_CLASS.test(targetStatic.class) && (
                  <a
                    href={`https://www.eveos.space/lukesguide/${targetStatic.class.toLowerCase()}/combat`}
                    target="_blank"
                    rel="noreferrer"
                    className="system-panel__icon-btn system-panel__icon-btn--guide"
                    aria-label={`Open Luke's combat guide for ${targetStatic.name}`}
                    title={`Open Luke's combat guide for ${targetStatic.name}`}
                  >
                    <Book size={16} aria-hidden />
                  </a>
                )}
                {gatecampHubs.map((entry) => (
                  <a
                    key={entry.hub}
                    href={`https://eve-gatecheck.space/eve/#${targetStatic.name}:${entry.hub}:shortest`}
                    target="_blank"
                    rel="noreferrer"
                    className="system-panel__icon-btn system-panel__icon-btn--gatecamp"
                    aria-label={`Check gatecamp route from ${targetStatic.name} to ${entry.hub}`}
                    title={`Check gatecamp route from ${targetStatic.name} to ${entry.hub}`}
                  >
                    <DoorOpen size={16} aria-hidden />
                    <span className="system-panel__icon-btn-label">
                      {entry.hub}
                    </span>
                  </a>
                ))}
              </span>
            </SystemIdentity>
          ) : (
            <span className="system-panel__heading-empty">
              No system focused
            </span>
          )}
        </h2>
        <button
          type="button"
          className="system-panel__icon-btn"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand system panel" : "Collapse system panel"}
        >
          {collapsed ? (
            <ChevronDown size={16} aria-hidden />
          ) : (
            <ChevronUp size={16} aria-hidden />
          )}
        </button>
      </header>
      <div className="system-panel__body">
        {!collapsed && !targetSystem && (
          <p className="system-panel__empty">
            Select a system, or jump in-game, to see its details.
          </p>
        )}
        {!collapsed && targetSystem && !targetStatic && (
          <p className="system-panel__empty">Loading system details…</p>
        )}
        {targetSystem && targetStatic && (
          <SystemDetails
            key={targetSystem.id}
            system={targetSystem}
            staticSystem={targetStatic}
            chainId={chainId}
            minimized={collapsed}
          />
        )}
      </div>
    </aside>
  );
}

interface SystemDetailsProps {
  system: System;
  staticSystem: StaticSystem;
  chainId: string;
  minimized?: boolean;
}

function SystemDetails({
  system,
  staticSystem,
  chainId,
  minimized,
}: SystemDetailsProps) {
  const updateSystem = useChainStore((s) => s.updateSystem);
  const jSpace = isJspaceClass(staticSystem.class);

  if (minimized) {
    if (!jSpace) return null;
    return (
      <p className="system-panel__statics">
        {staticSystem.statics && staticSystem.statics.length > 0
          ? staticSystem.statics.map((s) => (
              <span
                key={s.code}
                className={`system-panel__static system-class--${s.class}`}
              >
                <span className="system-panel__static-class">
                  {CLASS_LABEL[s.class]}
                </span>
                <span className="system-panel__static-code">{s.code}</span>
              </span>
            ))
          : "—"}
      </p>
    );
  }

  return (
    <>
      {jSpace && (
        <p className="system-panel__statics">
          {staticSystem.statics && staticSystem.statics.length > 0
            ? staticSystem.statics.map((s) => (
                <span
                  key={s.code}
                  className={`system-panel__static system-class--${s.class}`}
                >
                  <span className="system-panel__static-class">
                    {CLASS_LABEL[s.class]}
                  </span>
                  <span className="system-panel__static-code">{s.code}</span>
                </span>
              ))
            : "—"}
        </p>
      )}
      {jSpace && (
        <AnnotationTextSection
          system={system}
          chainId={chainId}
          updateSystem={updateSystem}
          title="Alias"
          field="alias"
        />
      )}
      {jSpace && (
        <OwnerSection
          system={system}
          chainId={chainId}
          updateSystem={updateSystem}
        />
      )}
      <AnnotationTextSection
        system={system}
        chainId={chainId}
        updateSystem={updateSystem}
        title="Notes"
        field="notes"
        multiline
      />
      {jSpace && (
        <StructuresSection
          system={system}
          chainId={chainId}
          updateSystem={updateSystem}
        />
      )}
    </>
  );
}

interface SectionProps {
  system: System;
  chainId: string;
  updateSystem: UpdateSystem;
}

function OwnerSection({ system, chainId, updateSystem }: SectionProps) {
  const owners = system.owners ?? [];
  const [ownerDialog, setOwnerDialog] = useState<
    { mode: "add" } | { mode: "edit"; owner: SystemOwner } | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  const apply = (patch: SystemAnnotationsPatch) => {
    setError(null);
    patchSystemAnnotations(chainId, system.id, patch)
      .then((res) => {
        updateSystem(system.id, {
          hostility: res.hostility ?? undefined,
          activity: res.activity ?? undefined,
          notes: res.notes ?? undefined,
          annotations_updated_at: res.annotations_updated_at ?? undefined,
        });
      })
      .catch((err) => {
        setError(
          err instanceof Error ? err.message : "Failed to save annotations.",
        );
      });
  };

  const updatedAt = latestTimestamp([
    system.annotations_updated_at,
    ...owners.map((r) => r.added_at),
  ]);

  return (
    <section className="system-panel__section">
      <div className="system-panel__section-header">
        <h3 className="system-panel__section-title">Owner</h3>
        {updatedAt && (
          <span className="system-panel__section-updated">
            Updated {formatDate(updatedAt)}
          </span>
        )}
      </div>
      {owners.map((owner) => (
        <OwnerRow
          key={`${owner.entity_type}:${owner.entity_id}`}
          system={system}
          chainId={chainId}
          owner={owner}
          updateSystem={updateSystem}
          onEdit={() => setOwnerDialog({ mode: "edit", owner })}
        />
      ))}
      {owners.length === 0 && (
        <button
          type="button"
          className="form__btn system-panel__add-btn"
          onClick={() => setOwnerDialog({ mode: "add" })}
        >
          Set Owner
        </button>
      )}
      <div className="hostility-badges">
        {HOSTILITY_BADGES.map((badge) => {
          const selected = system.hostility === badge.value;
          return (
            <button
              key={badge.value}
              type="button"
              className={`hostility-badge hostility-badge--${badge.value}${
                selected ? " is-selected" : ""
              }`}
              onClick={() =>
                apply({ hostility: selected ? null : badge.value })
              }
            >
              {badge.label}
            </button>
          );
        })}
      </div>
      <select
        className="form__input system-panel__activity"
        value={system.activity ?? ""}
        onChange={(e) =>
          apply({
            activity:
              e.target.value === ""
                ? null
                : (e.target.value as SystemActivity),
          })
        }
      >
        <option value="">—</option>
        {ACTIVITIES.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      {error && (
        <p className="form__hint form__hint--error" role="alert">
          {error}
        </p>
      )}
      {ownerDialog && (
        <OwnerDialog
          open
          onOpenChange={(o) => {
            if (!o) setOwnerDialog(null);
          }}
          chainId={chainId}
          system={system}
          updateSystem={updateSystem}
          editing={ownerDialog.mode === "edit" ? ownerDialog.owner : undefined}
        />
      )}
    </section>
  );
}

function AnnotationTextSection({
  system,
  chainId,
  updateSystem,
  title,
  field,
  multiline,
}: SectionProps & {
  title: string;
  field: "notes" | "alias";
  multiline?: boolean;
}) {
  const incoming = system[field] ?? "";
  const [draft, setDraft] = useState(incoming);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (timer.current == null) setDraft(incoming);
  }, [incoming]);

  const onChange = (value: string) => {
    setDraft(value);
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setError(null);
      const patch: SystemAnnotationsPatch =
        field === "notes" ? { notes: value } : { alias: value };
      patchSystemAnnotations(chainId, system.id, patch)
        .then((res) => {
          updateSystem(system.id, {
            hostility: res.hostility ?? undefined,
            activity: res.activity ?? undefined,
            notes: res.notes ?? undefined,
            alias: res.alias ?? undefined,
            annotations_updated_at: res.annotations_updated_at ?? undefined,
          });
        })
        .catch((err) => {
          setError(
            err instanceof Error
              ? err.message
              : `Failed to save ${title.toLowerCase()}.`,
          );
        });
    }, NOTES_DEBOUNCE_MS);
  };

  return (
    <section className="system-panel__section">
      <h3 className="system-panel__section-title">{title}</h3>
      {multiline ? (
        <textarea
          className="form__input"
          rows={4}
          value={draft}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="form__input"
          type="text"
          value={draft}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error && (
        <p className="form__hint form__hint--error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function StructuresSection({ system, chainId, updateSystem }: SectionProps) {
  const structures = system.structures ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const updatedAt = latestTimestamp(structures.map((s) => s.updated_at));

  return (
    <section className="system-panel__section">
      <div className="system-panel__section-header">
        <h3 className="system-panel__section-title">Structures</h3>
        {updatedAt && (
          <span className="system-panel__section-updated">
            Updated {formatDate(updatedAt)}
          </span>
        )}
      </div>
      {structures.map((structure) => (
        <StructureRow
          key={structure.structure_id}
          system={system}
          chainId={chainId}
          structure={structure}
          updateSystem={updateSystem}
        />
      ))}
      <button
        type="button"
        className="form__btn system-panel__add-btn"
        onClick={() => setDialogOpen(true)}
      >
        <Plus size={14} aria-hidden />
        Structure
      </button>
      {dialogOpen && (
        <StructureDialog
          open
          onOpenChange={(o) => {
            if (!o) setDialogOpen(false);
          }}
          chainId={chainId}
          system={system}
          updateSystem={updateSystem}
        />
      )}
    </section>
  );
}

interface StructureRowProps extends SectionProps {
  structure: SystemStructure;
}

function StructureRow({
  system,
  chainId,
  structure,
  updateSystem,
}: StructureRowProps) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<StructureKind>(structure.kind);
  const [name, setName] = useState(structure.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const StructureIcon = STRUCTURE_ICONS[structure.kind];

  const startEdit = () => {
    setKind(structure.kind);
    setName(structure.name);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await patchStructure(
        chainId,
        system.id,
        structure.structure_id,
        { kind, name },
      );
      updateSystem(system.id, {
        structures: (system.structures ?? []).map((s) =>
          s.structure_id === saved.structure_id ? saved : s,
        ),
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteStructure(chainId, system.id, structure.structure_id);
      updateSystem(system.id, {
        structures: (system.structures ?? []).filter(
          (s) => s.structure_id !== structure.structure_id,
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove.");
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="system-panel__row system-panel__row--edit">
        <select
          className="form__input"
          value={kind}
          onChange={(e) => setKind(e.target.value as StructureKind)}
        >
          {STRUCTURE_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input
          className="form__input"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="system-panel__row-actions">
          <button
            type="button"
            className="form__btn"
            onClick={() => setEditing(false)}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="form__btn"
            onClick={save}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
        {error && (
          <p className="form__hint form__hint--error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="system-panel__row">
      <div className="system-panel__structure">
        <StructureIcon
          size={14}
          className="system-panel__structure-icon"
          aria-hidden
        />
        <span className="system-panel__row-kind system-panel__structure-kind">
          {structure.kind}
        </span>
        <span className="system-panel__row-name">{structure.name || "—"}</span>
        <div className="system-panel__row-actions">
          <button
            type="button"
            className="system-panel__icon-btn"
            onClick={startEdit}
            disabled={busy}
            aria-label="Edit structure"
          >
            <Pencil size={14} aria-hidden />
          </button>
          <button
            type="button"
            className="system-panel__icon-btn"
            onClick={remove}
            disabled={busy}
            aria-label="Remove structure"
          >
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      </div>
      {error && (
        <p className="form__hint form__hint--error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface OwnerRowProps extends SectionProps {
  owner: SystemOwner;
  onEdit: () => void;
}

function OwnerRow({
  system,
  chainId,
  owner,
  updateSystem,
  onEdit,
}: OwnerRowProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteOwner(
        chainId,
        system.id,
        owner.entity_type,
        owner.entity_id,
      );
      updateSystem(system.id, {
        owners: (system.owners ?? []).filter(
          (r) =>
            !(
              r.entity_type === owner.entity_type &&
              r.entity_id === owner.entity_id
            ),
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove.");
      setBusy(false);
    }
  };

  return (
    <>
      <div className="system-panel__owner">
        <EntityLogo type={owner.entity_type} id={owner.entity_id} />
        <span className="system-panel__row-name">
          {owner.name ?? `#${owner.entity_id}`}
        </span>
        <span className="system-panel__row-kind">{owner.entity_type}</span>
        <div className="system-panel__row-actions">
          <button
            type="button"
            className="system-panel__icon-btn"
            onClick={onEdit}
            disabled={busy}
            aria-label="Change owner"
          >
            <Pencil size={14} aria-hidden />
          </button>
          <button
            type="button"
            className="system-panel__icon-btn"
            onClick={remove}
            disabled={busy}
            aria-label="Remove owner"
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      </div>
      {error && (
        <p className="form__hint form__hint--error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
